import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { updateDesProjectBody, getDesProjectBody } from '../../features/projects/desprojectSlice';
import { 
  Button, 
  Typography, 
  Card,
  message,
  Spin,
  Space,
  Tag
} from 'antd';
import {
  BgColorsOutlined,
  EditOutlined,
  StarFilled,
  StarOutlined,
} from '@ant-design/icons';
import Editor from '../other/Editor';

const { Title, Text } = Typography;

const EditDesForm = ({ 
  selectedProject, 
  onCancel, 
  onSave, 
  bodyContent, 
  setBodyContent 
}) => {
  const dispatch = useDispatch();
  const { bodyLoading, bodyError } = useSelector((state) => state.desprojects);

  // Helper function to check if content is truly empty
  const isContentEmpty = (content) => {
    if (!content) return true;
    
    // Remove HTML tags and check if any meaningful content remains
    const textContent = content
      .replace(/<[^>]*>/g, '') // Remove all HTML tags
      .replace(/&nbsp;/g, ' ') // Replace &nbsp; with space
      .trim(); // Remove whitespace
    
    return textContent === '';
  };

  const handleBodyChange = (content) => {
    setBodyContent(content);
  };

  const handleSaveContent = async () => {
    if (!selectedProject) {
      message.error('No project selected');
      return;
    }

    // Check if content is truly empty (not just HTML tags)
    const isEmpty = isContentEmpty(bodyContent);
    
    // If content is empty, save as empty string to clear the field
    const contentToSave = isEmpty ? '' : bodyContent;

    try {
      await dispatch(updateDesProjectBody({ 
        id: selectedProject._id, 
        body: contentToSave
      })).unwrap();
      
      message.success(isEmpty ? 'Case study cleared successfully!' : 'Case study saved successfully!');
      
      // Call the onSave callback if provided
      if (onSave) {
        onSave();
      }
    } catch (err) {
      console.error('Save error:', err);
      message.error('Failed to save case study: ' + (err.message || 'Unknown error'));
    }
  };

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    }
  };

  const getPriorityDisplay = (priority) => {
    switch (priority) {
      case 'featured':
        return <Tag color="gold" icon={<StarFilled />}>Featured Project</Tag>;
      case 'other':
        return <Tag color="purple" icon={<StarOutlined />}>Other Project</Tag>;
      default:
        return <Tag color="default">No Priority Set</Tag>;
    }
  };

  if (!selectedProject) {
    return (
      <Card className="mb-6">
        <div className="text-center py-12">
          <BgColorsOutlined className="text-6xl text-gray-400 mb-4" />
          <Title level={4} className="text-gray-500 mb-2">
            No Project Selected
          </Title>
          <Text className="text-gray-400 mb-6 block">
            Create your first design project to add a case study
          </Text>
        </div>
      </Card>
    );
  }

  return (
    <>
      {/* Selected Project Info */}
      <Card className="mb-6 bg-purple-50 border-purple-200">
        <div className="flex items-start space-x-4">
          <div className="flex-shrink-0">
            {selectedProject.url ? (
              <img
                src={selectedProject.url}
                alt={selectedProject.name}
                className="w-16 h-16 object-cover rounded-lg"
              />
            ) : (
              <div className="w-16 h-16 bg-gray-200 rounded-lg flex items-center justify-center">
                <BgColorsOutlined className="text-purple-600" />
              </div>
            )}
          </div>
          <div className="flex-1">
            <div className="flex items-center space-x-2 mb-2">
              <BgColorsOutlined className="text-purple-600" />
              <Title level={4} className="m-0">
                {selectedProject.name}
              </Title>
              <span className="bg-purple-100 text-purple-800 px-2 py-1 rounded text-xs">
                {selectedProject.type}
              </span>
              {getPriorityDisplay(selectedProject.priority)}
            </div>
            <Text className="text-gray-600 block mb-2">
              {selectedProject.desc}
            </Text>
            <Text className="text-purple-600 text-sm">
              Document your design process, user research, wireframes, and design decisions
            </Text>
          </div>
        </div>
      </Card>

      {/* Editor Section */}
      <Card>
        <div className="mb-4">
          <Title level={5} className="mb-2">Case Study Editor</Title>
          <Text className="text-gray-600 text-sm">
            Create a comprehensive case study including your design process, research, user personas, wireframes, prototypes, and design decisions.
          </Text>
        </div>
        
        {bodyLoading ? (
          <div className="text-center py-8">
            <Spin size="large" />
            <Text className="block mt-4 text-gray-500">Loading content...</Text>
          </div>
        ) : (
          <>
           <div className="editor-container border border-gray-200 rounded-lg" style={{ height: '700px', display: 'flex', flexDirection: 'column' }}>
              <Editor
                value={bodyContent}
                onChange={handleBodyChange}
                placeholder="Document your design process, user research findings, wireframes, prototypes, design iterations, user testing results, and final design decisions. Include problem statements, user personas, design systems, and lessons learned..."
                minHeight="400px"
                stickyToolbar={true}
                style={{ 
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column'
                }}
                editorStyle={{
                  height: 'calc(100% - 60px)', // Subtract toolbar height
                  overflow: 'auto',
                  border: 'none'
                }}
                toolbarStyle={{
                  position: 'sticky',
                  top: 0,
                  zIndex: 10,
                  backgroundColor: 'white',
                  borderBottom: '1px solid #e8e8e8',
                  borderRadius: '8px 8px 0 0'
                }}
                onClear={() => setBodyContent('')}
              />
            </div>

            {/* Media Rendering Guide Section */}
            <Card className="mt-4 bg-gradient-to-r from-purple-50 to-pink-50 border-purple-200">
              <div className="mb-3">
                <Title level={5} className="mb-2 text-purple-900 flex items-center">
                  <BgColorsOutlined className="mr-2" />
                  Media Rendering Guide
                </Title>
                <Text className="text-purple-700 text-sm">
                  Showcase your design work with different media display options
                </Text>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Regular Media */}
                <div className="bg-white p-4 rounded-lg border border-purple-100">
                  <div className="flex items-center mb-2">
                    <div className="w-3 h-3 bg-green-500 rounded-full mr-2"></div>
                    <Text strong className="text-green-700">Regular Media</Text>
                  </div>
                  <Text className="text-sm text-gray-600 mb-3">
                    Single images/videos display as centered blocks
                  </Text>
                  <div className="bg-gray-50 p-2 rounded text-xs font-mono">
                    Just paste the URL directly in text:<br/>
                    <span className="text-blue-600">https://example.com/wireframe.jpg</span>
                  </div>
                </div>

                {/* Carousel Media */}
                <div className="bg-white p-4 rounded-lg border border-purple-100">
                  <div className="flex items-center mb-2">
                    <div className="w-3 h-3 bg-purple-500 rounded-full mr-2"></div>
                    <Text strong className="text-purple-700">Carousel Media</Text>
                  </div>
                  <Text className="text-sm text-gray-600 mb-3">
                    Multiple designs in horizontal scrollable layout
                  </Text>
                  <div className="bg-gray-50 p-2 rounded text-xs font-mono">
                    Create a list with media URLs:<br/>
                    • <span className="text-blue-600">https://wireframe1.jpg</span><br/>
                    • <span className="text-blue-600">https://mockup1.jpg</span><br/>
                    • <span className="text-blue-600">https://prototype.mp4</span>
                  </div>
                </div>

                {/* Grid Media */}
                <div className="bg-white p-4 rounded-lg border border-purple-100">
                  <div className="flex items-center mb-2">
                    <div className="w-3 h-3 bg-orange-500 rounded-full mr-2"></div>
                    <Text strong className="text-orange-700">Grid Media</Text>
                  </div>
                  <Text className="text-sm text-gray-600 mb-3">
                    Multiple designs in organized grid layout
                  </Text>
                  <div className="bg-gray-50 p-2 rounded text-xs font-mono">
                    Start with layout config line:<br/>
                    • <span className="text-orange-600">layout=[grid] col=[3]</span><br/>
                    • <span className="text-blue-600">https://design1.jpg</span><br/>
                    • <span className="text-blue-600">https://design2.jpg</span><br/>
                    • <span className="text-blue-600">https://design3.jpg</span><br/>
                    • <span className="text-blue-600">https://design4.jpg</span>
                  </div>
                </div>
              </div>

              {/* Supported Formats */}
              <div className="mt-4 p-3 bg-white rounded-lg border border-purple-100">
                <Text strong className="text-purple-900 block mb-2">Supported Formats:</Text>
                <div className="flex flex-wrap gap-2">
                  <Tag color="green">Images: .jpg, .png, .gif, .webp, .svg</Tag>
                  <Tag color="blue">Videos: .mp4, .webm, .ogg, .mov</Tag>
                  <Tag color="red">YouTube: youtube.com or youtu.be links</Tag>
                </div>
              </div>

              {/* Design-Specific Pro Tips */}
              <div className="mt-3 p-3 bg-pink-50 rounded-lg border border-pink-200">
                <Text strong className="text-pink-800 block mb-2">🎨 Design Pro Tips:</Text>
                <ul className="text-sm text-pink-700 space-y-1 ml-4">
                  <li>• Use grids to showcase design iterations and variations</li>
                  <li>• Create carousels for user journey flows and wireframes</li>
                  <li>• Add descriptive text before media URLs for context (e.g., "Initial wireframes:")</li>
                  <li>• For grids: "layout=[grid] col=[3]" creates perfect design showcase</li>
                  <li>• Mix screenshots, mockups, and prototype videos in one gallery</li>
                  <li>• Use lists with 4+ media items to get automatic navigation</li>
                  <li>• All layouts automatically stack to single column on mobile</li>
                  <li>• Perfect for before/after comparisons and design evolution</li>
                </ul>
              </div>
            </Card>

            <div className="flex justify-between items-center mt-6 pt-4 border-t border-gray-200">
              <Text className="text-gray-500 text-sm">
                {bodyContent.length > 0 ? `${bodyContent.length} characters` : 'Start writing your case study...'}
              </Text>
              <Space>
                <Button onClick={handleCancel}>
                  Cancel
                </Button>
                <Button 
                  type="primary" 
                  onClick={handleSaveContent}
                  loading={bodyLoading}
                  className="bg-purple-600 hover:bg-purple-700"
                >
                  {isContentEmpty(bodyContent) ? 'Clear Case Study' : 'Save Case Study'}
                </Button>
              </Space>
            </div>
          </>
        )}

        {bodyError && (
          <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
            <Text className="text-red-600">Error: {bodyError}</Text>
          </div>
        )}
      </Card>
    </>
  );
};

export default EditDesForm;